import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { createPersistentItem, globalRef } from '@amazon/vinyl-util'
import { boolean } from '@amazon/vinyl-validation'
import { ctx } from '@/util/context'
import { globalData } from '@amzn/amp-playback'

export interface PersistedToggle {
    readonly enabled$: ObservableValue<boolean>
    readonly write: (next: boolean) => Promise<void>
}

/**
 * A validated, locally persisted on/off preference. A corrupt stored value
 * falls back to `defaultValue`.
 */
export function persistedToggle(key: string, defaultValue: boolean): PersistedToggle {
    const { errorHandler } = ctx(key)
    const item = createPersistentItem<boolean>({
        key,
        defaultValue,
        validator: boolean().assert,
    })
    // Persistence is async; seed the default and hydrate once resolved, unless a
    // write landed first — the stored value it would restore is older than that write.
    const state = globalRef(() => {
        const current = { enabled$: data(defaultValue), written: false }
        item.get()
            .then((v) => {
                if (!current.written) current.enabled$.value = v
            })
            .catch(errorHandler)
        return current
    })
    const enabled$ = globalData(() => state.value.enabled$)
    enabled$.initialize()
    return {
        enabled$,
        write: (next) => {
            state.value.written = true
            enabled$.value = next
            return item.set(next)
        },
    }
}
